import React, { useEffect, useRef, useState } from "react";
import "./Offline.scss";
import { useOffline } from "../../contexts/OfflineContext";
import RestClient from "../../@core/restClient";
import { Songbook } from "../../@core/models";
import { useNavigate } from "react-router-dom";
import { IoChevronDown, IoChevronUp, IoSearchOutline } from "react-icons/io5";
import { saveSongsToDB, deleteOfflineDB } from "../../utils/offlineDB";
import { registerSW, unregisterSW } from "../../utils/swManager";

const OFFLINE_BOOK_KEY = "offline_saved_book";

interface SavedBookMeta {
  id: number;
  title: string;
  songCount: number;
  savedAt: string;
  songbookSize?: number;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const getCacheSize = async (): Promise<number> => {
  if (!("caches" in window)) return 0;
  const names = await caches.keys();
  let total = 0;
  for (const name of names) {
    const cache = await caches.open(name);
    const keys = await cache.keys();
    const responses = await Promise.all(keys.map((k) => cache.match(k)));
    for (const res of responses) {
      if (res) {
        const buf = await res.clone().arrayBuffer();
        total += buf.byteLength;
      }
    }
  }
  return total;
};

const Offline = () => {
  const { offlineEnabled, toggleOffline } = useOffline();
  const navigate = useNavigate();
  const [songbooks, setSongbooks] = useState<Songbook[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [savedBook, setSavedBook] = useState<SavedBookMeta | null>(null);
  const [userBookCount, setUserBookCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [cacheSize, setCacheSize] = useState<number | null>(null);
  const [noBookMsg, setNoBookMsg] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bookSearch, setBookSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const userId = JSON.parse(localStorage.getItem("User") || "{}")?.id;
        const [publicList, userList] = await Promise.all([
          RestClient.getSongbookList(""),
          userId ? RestClient.getSongBooks(userId) : Promise.resolve([])
        ]);
        const userBooks: Songbook[] = (userList || []).sort((a: Songbook, b: Songbook) =>
          a.title.localeCompare(b.title, "uk")
        );
        const userIds = new Set(userBooks.map((sb: Songbook) => sb.id));
        const publicBooks: Songbook[] = (publicList || [])
          .filter((sb: Songbook) => !userIds.has(sb.id))
          .sort((a: Songbook, b: Songbook) => a.title.localeCompare(b.title, "uk"));
        setUserBookCount(userBooks.length);
        setSongbooks([...userBooks, ...publicBooks]);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(OFFLINE_BOOK_KEY);
    if (stored) setSavedBook(JSON.parse(stored));
    getCacheSize().then(setCacheSize);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    setTimeout(() => searchInputRef.current?.focus(), 50);
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setBookSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const handleToggle = () => {
    if (!offlineEnabled && !savedBook) {
      setNoBookMsg(true);
      return;
    }
    setNoBookMsg(false);
    toggleOffline();
  };

  const handleSave = async () => {
    if (!selectedId) return;
    const book = songbooks.find((sb) => sb.id === Number(selectedId));
    if (!book) return;
    setIsSaving(true);
    try {
      const songs = await RestClient.getSongList(selectedId, undefined, undefined, 1, 250);
      const meta: SavedBookMeta = {
        id: book.id,
        title: book.title,
        songCount: songs.length,
        savedAt: new Date().toLocaleDateString("uk-UA"),
        songbookSize: new Blob([JSON.stringify(songs)]).size
      };
      localStorage.setItem(OFFLINE_BOOK_KEY, JSON.stringify(meta));
      setSavedBook(meta);
      await saveSongsToDB(songs);
      await registerSW();
      getCacheSize().then(setCacheSize);
    } catch (e) {
      console.log(e);
    }
    setIsSaving(false);
  };

  const handleUpdate = async () => {
    if (!savedBook) return;
    setIsUpdating(true);
    try {
      const songs = await RestClient.getSongList(String(savedBook.id), undefined, undefined, 1, 250);
      const meta: SavedBookMeta = {
        ...savedBook,
        songCount: songs.length,
        savedAt: new Date().toLocaleDateString("uk-UA"),
        songbookSize: new Blob([JSON.stringify(songs)]).size
      };
      localStorage.setItem(OFFLINE_BOOK_KEY, JSON.stringify(meta));
      setSavedBook(meta);
      await saveSongsToDB(songs);
      await registerSW();
      getCacheSize().then(setCacheSize);
    } catch (e) {
      console.log(e);
    }
    setIsUpdating(false);
  };

  const handleRemove = async () => {
    localStorage.removeItem(OFFLINE_BOOK_KEY);
    setSavedBook(null);
    setSelectedId("");
    setCacheSize(null);
    if (offlineEnabled) toggleOffline();
    try { await deleteOfflineDB(); } catch (e) { console.error(e); }
    try { await unregisterSW(); } catch (e) { console.error(e); }
  };


  return (
    <div className="offline">

      {/* Slider row */}
      <div className="offline__row">
        <div className="offline__info">
          <div className="offline__title-row">
            <p className="offline__label">Офлайн режим</p>
            <div
              className={`offline__switch ${offlineEnabled ? "offline__switch--on" : ""}`}
              onClick={handleToggle}
            >
              <div className="offline__switch-thumb" />
            </div>
          </div>
          <p className="offline__desc">
            Зберігає застосунок і один пісенник у браузері для роботи без інтернету.
          </p>
          <p className="offline__desc">
            В офлайн режимі деякі функції не працюватимуть.
          </p>
          {offlineEnabled && savedBook && (
            <button
              className="btn-mcolor offline__go-btn"
              onClick={() => navigate(`/songbook/${savedBook.id}`)}
            >
              Перейти до пісенника
            </button>
          )}
        </div>
      </div>

      {noBookMsg && (
        <p className="offline__warning">
          Спочатку оберіть і збережіть пісенник нижче
        </p>
      )}

      {/* Songbook picker — only in online mode */}
      {!offlineEnabled && <div className="offline__section">
        <p className="offline__section-title">Пісенник для офлайн режиму</p>
        <div className="offline__dropdown" ref={dropdownRef}>
          <div
            className={`offline__dropdown-trigger${dropdownOpen ? " offline__dropdown-trigger--open" : ""}`}
            onClick={() => { setDropdownOpen((p) => !p); setNoBookMsg(false); }}
          >
            <span className={selectedId ? "" : "offline__dropdown-placeholder"}>
              {selectedId
                ? songbooks.find((sb) => sb.id === Number(selectedId))?.title
                : "— Оберіть пісенник —"}
            </span>
            {dropdownOpen ? <IoChevronUp /> : <IoChevronDown />}
          </div>
          <div className={`offline__dropdown-menu${dropdownOpen ? " offline__dropdown-menu--open" : ""}`}>
            <div className="offline__dropdown-search">
              <IoSearchOutline className="offline__dropdown-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="offline__dropdown-search-input"
                placeholder="Знайти пісенник..."
                value={bookSearch}
                onChange={(e) => setBookSearch(e.target.value)}
              />
            </div>
            <div className="offline__dropdown-list">
              {(() => {
                const filtered = songbooks.filter((sb) =>
                  sb.title.toLowerCase().includes(bookSearch.toLowerCase())
                );
                const items: React.ReactNode[] = [];
                filtered.forEach((sb, i) => {
                  if (i === userBookCount && userBookCount > 0 && !bookSearch) {
                    items.push(
                      <div key="sep" className="offline__dropdown-sep">Публічні пісенники</div>
                    );
                  }
                  items.push(
                    <div
                      key={sb.id}
                      className={`offline__dropdown-item${Number(selectedId) === sb.id ? " offline__dropdown-item--selected" : ""}`}
                      onClick={() => { setSelectedId(String(sb.id)); setDropdownOpen(false); setBookSearch(""); }}
                    >
                      {sb.title}
                    </div>
                  );
                });
                return items;
              })()}
            </div>
          </div>
        </div>
        <button
          className="offline__btn offline__btn--save"
          onClick={handleSave}
          disabled={!selectedId || isSaving}
        >
          {isSaving ? <><span className="offline__spinner" /> Завантажується...</> : "Завантажити"}
        </button>
      </div>}

      {/* Saved book stats */}
      {savedBook && (
        <div className="offline__section offline__section--saved">
          <p className="offline__section-title">Збережено</p>
          <div className="offline__stat-row">
            <span className="offline__stat-label">Пісенник</span>
            <span className="offline__stat-value">{savedBook.title}</span>
          </div>
          <div className="offline__stat-row">
            <span className="offline__stat-label">Пісень</span>
            <span className="offline__stat-value">{savedBook.songCount}</span>
          </div>
          <div className="offline__stat-row">
            <span className="offline__stat-label">Розмір пісенника</span>
            <span className="offline__stat-value">
              {savedBook.songbookSize ? formatBytes(savedBook.songbookSize) : "—"}
            </span>
          </div>
          <div className="offline__stat-row">
            <span className="offline__stat-label">Кеш додатку</span>
            <span className="offline__stat-value">
              {cacheSize === null ? "—" : formatBytes(cacheSize)}
            </span>
          </div>
          <div className="offline__stat-row">
            <span className="offline__stat-label">Збережено</span>
            <span className="offline__stat-value">{savedBook.savedAt}</span>
          </div>
          <p className="offline__note">
            Видалення пісенника з кеша браузера не вплине на роботу застосунку в онлайн режимі. Якщо пісенник для офлайн режиму було завантажено давно, оновіть його версію.
          </p>
          <div className="offline__actions">
            <button
              className="btn-mcolor"
              onClick={handleUpdate}
              disabled={isUpdating || offlineEnabled}
            >
              {isUpdating ? <><span className="offline__spinner offline__spinner--light" /> Оновлюється...</> : "Оновити"}
            </button>
            <button
              className="btn-plain"
              onClick={handleRemove}
              disabled={offlineEnabled}
            >
              Видалити
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Offline;
